import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { stripHtml } from '../../lib/rich-text';
import { ageDisplay, formatPathologyDateTime } from './pathology.utils';
import { usePathologyReports } from './pathology.queries';
import type { InvestigationReportKind, PathologyPatientSnapshot, PathologyReportDoctor, PathologyReportLine, PathologyReportResult } from './types';

interface HiddenPrintSections {
  result: boolean;
  instrument: boolean;
  reference: boolean;
  unit: boolean;
  method: boolean;
  sample: boolean;
  note: boolean;
  extraNote: boolean;
  attachment: boolean;
  employeeId: boolean;
}

const defaultHidden: HiddenPrintSections = {
  result: false,
  instrument: false,
  reference: false,
  unit: false,
  method: false,
  sample: false,
  note: false,
  extraNote: false,
  attachment: false,
  employeeId: false,
};

const numberOrNull = (value: string | null) => {
  if (value === null || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const allRangesEqual = (result: PathologyReportResult) =>
  result.maleLower === result.femaleLower
  && result.maleLower === result.childLower
  && result.maleUpper === result.femaleUpper
  && result.maleUpper === result.childUpper;

const rangeFor = (patient: PathologyPatientSnapshot, result: PathologyReportResult) => {
  if (allRangesEqual(result)) return { lower: result.maleLower, upper: result.maleUpper };
  if (patient.ageYears > 12 && patient.gender === 'Male') return { lower: result.maleLower, upper: result.maleUpper };
  if (patient.ageYears > 12) return { lower: result.femaleLower, upper: result.femaleUpper };
  return { lower: result.childLower, upper: result.childUpper };
};

const isAbnormal = (patient: PathologyPatientSnapshot, result: PathologyReportResult) => {
  const value = numberOrNull(result.resultValue);
  const range = rangeFor(patient, result);
  const lower = numberOrNull(range.lower);
  const upper = numberOrNull(range.upper);
  return value !== null && lower !== null && upper !== null && (value < lower || value > upper);
};

const referenceRangeText = (result: PathologyReportResult) => {
  if (allRangesEqual(result)) return [result.maleLower, result.maleUpper].filter(Boolean).join(' - ');
  return [
    result.maleLower && result.maleUpper ? `Male: ${result.maleLower} - ${result.maleUpper}` : '',
    result.femaleLower && result.femaleUpper ? `Female: ${result.femaleLower} - ${result.femaleUpper}` : '',
    result.childLower && result.childUpper ? `Child: ${result.childLower} - ${result.childUpper}` : '',
  ].filter(Boolean).join('\n');
};

const uniqueDoctors = (reports: PathologyReportLine[]) => {
  const map = new Map<string, PathologyReportDoctor>();
  reports.forEach((report) => report.approvedDoctors.forEach((doctor) => map.set(doctor.id, doctor)));
  return [...map.values()].slice(0, 3);
};

const hasTemplateResult = (report: PathologyReportLine) => Boolean(report.reportTemplateResult);

const hasPrintableResult = (result: PathologyReportResult) => {
  const value = result.resultValue;
  return value !== null && value !== '' && value !== '0';
};

const uniqueInstruments = (reports: PathologyReportLine[]) =>
  [...new Set(reports.map((report) => stripHtml(report.instrumentUsed)).filter((value): value is string => Boolean(value)))].join(', ');

const firstNonTemplateDepartment = (reports: PathologyReportLine[]) =>
  reports.find((report) => !hasTemplateResult(report) && report.departmentName)?.departmentName ?? null;

const isPathologyReportKind = (kind: InvestigationReportKind | null | undefined) =>
  kind !== 'non-pathology' && kind !== 'radiology';

const isImageAttachment = (dataUrl: string) => /^data:image\//i.test(dataUrl);

const PrintControls = ({
  hidden,
  onChange,
  space,
  onSpaceChange,
  onClose,
}: {
  hidden: HiddenPrintSections;
  onChange: (hidden: HiddenPrintSections) => void;
  space: number;
  onSpaceChange: (space: number) => void;
  onClose: () => void;
}) => {
  const toggle = (key: keyof HiddenPrintSections) => onChange({ ...hidden, [key]: !hidden[key] });
  return (
    <div className="pathology-print-controls" id="printButton">
      <div>
        <label><input type="checkbox" checked={!hidden.result} onChange={() => toggle('result')} /> Result</label>
        <label><input type="checkbox" checked={!hidden.instrument} onChange={() => toggle('instrument')} /> Instrument Used</label>
        <label><input type="checkbox" checked={!hidden.reference} onChange={() => toggle('reference')} /> Referrence Range</label>
        <label><input type="checkbox" checked={!hidden.unit} onChange={() => toggle('unit')} /> Test Unit</label>
        <label><input type="checkbox" checked={!hidden.method} onChange={() => toggle('method')} /> Method</label>
        <label><input type="checkbox" checked={!hidden.sample} onChange={() => toggle('sample')} /> Sample Type</label>
        <label><input type="checkbox" checked={!hidden.note} onChange={() => toggle('note')} /> N.B</label>
        <label><input type="checkbox" checked={!hidden.extraNote} onChange={() => toggle('extraNote')} /> Extra Note</label>
        <label><input type="checkbox" checked={!hidden.attachment} onChange={() => toggle('attachment')} /> Selected Image</label>
        <label><input type="checkbox" checked={!hidden.employeeId} onChange={() => toggle('employeeId')} /> Doctor Registration No</label>
        <span>|| Add Space </span>
        <input value={space} onChange={(event) => onSpaceChange(Number(event.target.value) || 0)} />
      </div>
      <div className="pathology-print-control-actions">
        <button type="button" onClick={() => window.print()}><b>Print</b></button>
        <button type="button" onClick={onClose}><b>Close</b></button>
      </div>
    </div>
  );
};

export const PathologyReportPrintPage = () => {
  const { billId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const lineIds = useMemo(() => (params.get('lineIds') ?? '').split(',').map((id) => id.trim()).filter(Boolean), [params]);
  const query = usePathologyReports(billId, lineIds);
  const [hidden, setHidden] = useState(defaultHidden);
  const [space, setSpace] = useState(0);
  const closePrintPage = () => {
    window.close();
    window.setTimeout(() => {
      if (!window.closed) navigate('/pathology');
    }, 100);
  };

  if (lineIds.length === 0) return <ErrorState title="No reports selected" message="Select one or more reports to print." />;
  if (query.isLoading) return <LoadingState />;
  if (query.isError || !query.data) return <ErrorState title="Report not found" message="The selected report could not be loaded." />;
  if (query.data.reports.length === 0) return <EmptyState title="No report lines found" message="The selected bill has no matching report rows." />;

  const bundle = query.data;
  const reports = bundle.reports;
  const first = reports[0];
  const patient = bundle.patientSnapshot;
  const approvedDoctorIds = new Set(reports.flatMap((report) => report.approvedDoctors.map((doctor) => doctor.id)));
  const doctors = bundle.approvalDoctorOptions.length > 0 ? bundle.approvalDoctorOptions.slice(0, 3) : uniqueDoctors(reports);
  const instruments = uniqueInstruments(reports);
  const department = reports.length === 1 ? firstNonTemplateDepartment(reports) : null;
  const reportKind = first?.reportKind ?? null;
  const showResultHeading = first ? !hasTemplateResult(first) : false;
  const firstExtraNote = first?.extraNote ?? null;
  const firstSample = reports.find((report) => report.sample)?.sample ?? null;
  const attachments = reports.filter((report) => report.attachment);

  return (
    <div className="pathology-report-print-page">
      <PrintControls hidden={hidden} onChange={setHidden} space={space} onSpaceChange={setSpace} onClose={closePrintPage} />

      <div className="pathology-print-letterhead-space" style={{ height: space }} />
      <table className="pathology-print-frame">
        <thead>
          <tr>
            <td className="pathology-print-header-left">
              <b className="pathology-print-patient-name">{patient.name}</b><br />
              <b>UHID :</b> {patient.uhid ?? bundle.patientId ?? '-'}<br />
              <b>Gender :</b> {patient.gender}<br />
              <b>Age :</b> {ageDisplay(patient)}<br />
              {bundle.referringDoctor?.name ? <><b>Under Doctor : </b> Dr. {bundle.referringDoctor.name}<br /></> : null}
              {!bundle.referringDoctor?.name && bundle.referredBy?.name ? <><b>Referred By : </b> {bundle.referredBy.name}<br /></> : null}
            </td>
            <td className="pathology-print-header-mid">
              <b>Bill Date : </b> {formatPathologyDateTime(bundle.billDate)}<br />
              <b> Report Date : </b> {formatPathologyDateTime(new Date().toISOString())}
            </td>
            <td className="pathology-print-header-right">
              <span className="pathology-print-barcode" aria-label="Patient barcode" /><br />
              {first?.sampleCollectedAt ? <><b>Collection Date : {formatPathologyDateTime(first.sampleCollectedAt)}</b><br /></> : null}
              {first?.labReceivedAt ? <><b>Lab Received Date : {formatPathologyDateTime(first.labReceivedAt)}</b><br /></> : null}
            </td>
          </tr>
          <tr>
            <td colSpan={3} className="pathology-print-no-pad">
              <table className="pathology-print-title-table">
                <tbody>
                  <tr>
                    <th>
                      {reportKind === 'radiology' ? 'DEPARTMENT OF RADIOLOGY' : 'LABORATORY REPORT'}
                      {isPathologyReportKind(reportKind) && department ? <><br /><span>DEPARTMENT OF {department}</span></> : null}
                    </th>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </thead>

        <tbody>
          {showResultHeading ? (
            <tr>
              <td colSpan={3} className="pathology-print-no-pad">
                <table className="pathology-print-result-head">
                  <tbody>
                    <tr>
                      <td>Test Name</td>
                      {!hidden.result ? <td>Result</td> : null}
                      {!hidden.unit ? <td>Unit</td> : null}
                      {!hidden.reference ? <td>Reference Range</td> : null}
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          ) : null}

          {reports.map((report) => (
            hasTemplateResult(report) ? (
              <tr key={report.lineId}>
                <td colSpan={3} className="pathology-print-no-pad">
                  <div className="pathology-print-template-result" dangerouslySetInnerHTML={{ __html: report.reportTemplateResult ?? '' }} />
                </td>
              </tr>
            ) : (
              <tr key={report.lineId}>
                <td colSpan={3} className="pathology-print-no-pad">
                  <table className="pathology-print-result-table">
                    <tbody>
                      {report.reportResults.filter(hasPrintableResult).map((result, index) => (
                        <tr key={result.id}>
                          <td>
                            {result.label === 'Group' ? result.testParameter : index === 0 ? <b>{result.testParameter}</b> : result.testParameter}
                            {!hidden.method && result.method ? <><br /><span>Method : {result.method}</span></> : null}
                          </td>
                          {!hidden.result ? <td>{isAbnormal(patient, result) ? <b>{result.resultValue}</b> : result.resultValue}</td> : null}
                          {!hidden.unit ? <td>{result.unit ?? ''}</td> : null}
                          {!hidden.reference ? <td>{referenceRangeText(result)}</td> : null}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <br /><br />
                </td>
              </tr>
            )
          ))}

          {!hidden.note ? reports.map((report) => report.note ? (
            <tr key={`${report.lineId}-note`}>
              <td colSpan={3}>
                <div className="pathology-print-note"><b>N.B. <small>({report.chargeName})</small> : </b><span dangerouslySetInnerHTML={{ __html: report.note }} /></div>
              </td>
            </tr>
          ) : null) : null}

          {!hidden.extraNote && firstExtraNote ? (
            <tr>
              <td colSpan={3}>
                <div className="pathology-print-note"><b>Extra Note: </b><span dangerouslySetInnerHTML={{ __html: firstExtraNote }} /></div>
              </td>
            </tr>
          ) : null}

          {!hidden.instrument && instruments ? (
            <tr>
              <td colSpan={3}><p className="pathology-print-note"><b>Instrument Used :</b> {instruments}</p></td>
            </tr>
          ) : null}

          {!hidden.sample && firstSample ? (
            <tr>
              <td colSpan={3}><p className="pathology-print-note"><b>Sample Type :</b> {firstSample}</p></td>
            </tr>
          ) : null}

          {!hidden.attachment && attachments.length > 0 ? attachments.map((report) => report.attachment ? (
            <tr key={`${report.lineId}-attachment`}>
              <td colSpan={3}>
                <div className="pathology-print-attachment">
                  <b>{report.chargeName}</b>
                  {isImageAttachment(report.attachment.dataUrl) ? (
                    <img src={report.attachment.dataUrl} alt={report.attachment.name} />
                  ) : (
                    <a href={report.attachment.dataUrl} download={report.attachment.name}>{report.attachment.name}</a>
                  )}
                </div>
              </td>
            </tr>
          ) : null) : null}

          <tr>
            <td colSpan={3}><p className="pathology-print-end">***** End Of Report *****</p></td>
          </tr>
        </tbody>

        <tfoot>
          <tr>
            <td colSpan={3}>
              <div className="pathology-print-signature-container">
                <table>
                  <tbody>
                    <tr>
                      {doctors.map((doctor) => (
                        <td key={doctor.id}>
                          <div className="pathology-print-signature-space">
                            {approvedDoctorIds.has(doctor.id) && doctor.signatureDataUrl ? <img src={doctor.signatureDataUrl} alt={`Dr. ${doctor.name} signature`} /> : <span />}
                          </div>
                          <strong>DR. {doctor.name.toUpperCase()}</strong><br />
                          {doctor.qualification ? <><span>{doctor.qualification}</span><br /></> : null}
                          {doctor.department ? <span>{doctor.department}</span> : null}{doctor.department && doctor.specialization ? <span>, </span> : null}{doctor.specialization ? <><span>{doctor.specialization}</span><br /></> : null}
                          {!hidden.employeeId && doctor.registrationId && doctor.registrationId !== 'Ph D' ? <><span className="employee_id_hide">Reg No: {doctor.registrationId}</span><br /></> : null}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
                <table className="pathology-print-footer">
                  <tbody>
                    <tr>
                      <td><b>Print By : {bundle.printedBy.name || '-'}</b></td>
                      <td>Comments : Please Correlate with Clinical Conditions</td>
                      <td><b>Checked By</b></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};
